import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, CheckCircle2, AlertCircle } from 'lucide-react';

const Careers = () => {
  const endpoint = process.env.REACT_APP_FORMSPREE_CAREERS_ENDPOINT;

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!endpoint) {
      setStatus('missing_endpoint');
      return;
    }

    try {
      setStatus('submitting');
      const formData = new FormData(e.currentTarget);

      const res = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Submission failed');
      }

      e.currentTarget.reset();
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Submission failed');
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-navy-950 text-white">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-r from-[#f97316] to-[#fbce16] mb-6">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-5">
                Join <span className="text-gradient-blue">BeeMind AI</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                We build sovereign AI infrastructure for global finance — private LLM hosting, precision retrieval, and
                AI-native data platforms.
              </p>
              <div className="rounded-2xl border border-slate-700/40 bg-white/5 p-6 premium-border premium-elevate">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-slate-200 font-semibold mb-1">Resume submission</p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      Your resume will be sent to{' '}
                      <a className="text-orange-300 hover:text-orange-200" href="mailto:info@beemind.ai">
                        info@beemind.ai
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-700/40 bg-navy-900/40 p-6 sm:p-8 shadow-2xl shadow-orange-900/10 premium-border premium-elevate">
              <h2 className="text-xl font-bold mb-5">Apply now</h2>

              {status === 'success' && (
                <div className="mb-5 rounded-xl border border-green-500/30 bg-green-500/10 p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-200">Application submitted</p>
                      <p className="text-sm text-slate-200/80">We’ll get back to you soon.</p>
                    </div>
                  </div>
                </div>
              )}

              {status === 'missing_endpoint' && (
                <div className="mb-5 rounded-xl border border-orange-500/30 bg-orange-500/10 p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-300 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-orange-100">Form backend not configured</p>
                      <p className="text-sm text-slate-200/80">
                        To enable uploads, set{' '}
                        <span className="font-semibold">REACT_APP_FORMSPREE_CAREERS_ENDPOINT</span>. For now, email your
                        resume to{' '}
                        <a className="text-orange-300 hover:text-orange-200" href="mailto:info@beemind.ai">
                          info@beemind.ai
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-300 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-100">Submission failed</p>
                      <p className="text-sm text-slate-200/80 break-words">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={onSubmit} encType="multipart/form-data" className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-200 mb-1" htmlFor="name">Full name</label>
                  <input
                    id="name"
                    name="name"
                    required
                    className="w-full rounded-lg bg-white/5 border border-slate-700/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-200 mb-1" htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full rounded-lg bg-white/5 border border-slate-700/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                    placeholder="you@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-200 mb-1" htmlFor="role">Role of interest</label>
                  <input
                    id="role"
                    name="role"
                    className="w-full rounded-lg bg-white/5 border border-slate-700/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                    placeholder="e.g., Founding Engineer, ML Engineer, Product"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-200 mb-1" htmlFor="message">Message (optional)</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="w-full rounded-lg bg-white/5 border border-slate-700/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
                    placeholder="Tell us what you’re building / why BeeMind"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-200 mb-1" htmlFor="resume">Resume</label>
                  <input
                    id="resume"
                    name="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    required
                    className="block w-full text-sm text-slate-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-white file:text-gray-900 hover:file:bg-gray-100"
                  />
                  <p className="mt-1 text-xs text-slate-400">Accepted formats: PDF, DOC, DOCX</p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white text-sm font-semibold shadow-lg shadow-orange-500/20 transition-all duration-300 transform hover:scale-[1.01] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {status === 'submitting' ? 'Submitting…' : 'Submit application'}
                </button>

                <p className="text-xs text-slate-400 leading-relaxed">
                  By submitting, you consent to BeeMind AI contacting you about your application.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
